/**
 * AMD-13 — the run event log, in the dwarf.land readout register: a leading icon at
 * text size, a mono timestamp, an underlined subject, and a coloured bracketed delta.
 *
 * Split in two on purpose:
 *
 *   `EventLogStore` is the recorder. It subscribes read-only to the §A.3 bus and runs
 *   for the whole run, so it lives on the hot path and allocates nothing per event.
 *   A record holds a numeric kind, a tick, a *reference* to a string the payload
 *   already owns, and two numbers. No template literal, no object literal, no array
 *   push — the ring is pre-allocated at construction and slots are overwritten.
 *
 *   `EventLogView` is the reader. It owns a fixed pool of row elements built once and
 *   only ever rewritten by `setText`. `render()` runs when a panel opens, never during
 *   a run, which is the one place formatting a string is affordable.
 *
 * One store feeds many views: the Pause screen and the Run Summary each hold their own
 * view over the same records, because a DOM node cannot be in two panels at once.
 */

import { bossById, enemyById, evolutionById, passiveById, weaponById } from '../core/content';
import type { EventBus } from '../core/events';
import { div, icon, span } from './dom';
import { formatInt, formatTime, humanizeId } from './format';

/** Record kinds. Numeric so a record never holds a freshly built string. */
export const enum LogKind {
  None = 0,
  LevelUp = 1,
  Pick = 2,
  Evolution = 3,
  SurgeChange = 4,
  BossSpawn = 5,
  BossDefeated = 6,
  EliteDown = 7,
  PhaseChange = 8,
  Hazard = 9,
  Pickup = 10,
  /** AMD-34 — a numbered wave opened. `a` is the wave, `b` is 1 on a boss wave. */
  WaveChange = 11,
  /** AMD-35 — a Graft resolved. `id` is the rarity, `a`/`b` the levels it moved. */
  Graft = 12,
  /**
   * AMD-46 — §14.1 B3 clipped a split boon's burst. `id` is the boon's `source` key,
   * `a` is what the build asked for and `b` is what the field had room for.
   */
  BudgetTrim = 13,
  /** AMD-51 — an anomaly opened (`b` 1) or closed (`b` 0). `a` is its danger 1..3. */
  Anomaly = 14,
  /** AMD-53 — a boss salvage spin. `id` is the tier, `a` the ichor paid. */
  Salvage = 15,
}

/** §13 filter strip, dwarf.land's `ALL | UNCOMMON | NOTABLE | RARE` in this domain. */
export type LogChannel = 'build' | 'threat' | 'world';

const CHANNEL_OF: readonly LogChannel[] = [
  'world', // None — never rendered
  'build', // LevelUp
  'build', // Pick
  'build', // Evolution
  'build', // SurgeChange
  'threat', // BossSpawn
  'threat', // BossDefeated
  'threat', // EliteDown
  'world', // PhaseChange
  'world', // Hazard
  'world', // Pickup
  'world', // WaveChange
  'build', // Graft
  'build', // BudgetTrim — it is a fact about your build, not about the trench
];

const ICON_OF: readonly string[] = [
  'circle-notch',
  'chart-line', // LevelUp
  'star', // Pick
  'sparkles', // Evolution
  'bolt', // SurgeChange
  'crown', // BossSpawn
  'trophy', // BossDefeated
  'hockey-mask', // EliteDown
  'globe', // PhaseChange
  'exclamation-triangle', // Hazard
  'heart', // Pickup
  'flag', // WaveChange
  'seedlings', // Graft
  'filter', // BudgetTrim — something was let through and something was held back
];

/** §11.2.6 semantic families. A delta's colour means the same thing everywhere. */
type DeltaTone = 'xp' | 'hp' | 'surge' | 'hazard' | 'dim';

const TONE_OF: readonly DeltaTone[] = [
  'dim',
  'xp', // LevelUp
  'surge', // Pick
  'surge', // Evolution
  'surge', // SurgeChange
  'hp', // BossSpawn
  'xp', // BossDefeated
  'xp', // EliteDown
  'dim', // PhaseChange
  'hazard', // Hazard
  'xp', // Pickup
  'dim', // WaveChange
  'surge', // Graft
  'dim', // BudgetTrim — a denial, not a loss; the tone must not read as damage
];

interface LogRecord {
  kind: LogKind;
  tick: number;
  /** A string the event payload already owns. Assigning it copies a pointer. */
  id: string;
  a: number;
  b: number;
}

const CAPACITY = 64;

/**
 * AMD-46 — ticks between two logged trims.
 *
 * `budget_trim` fires several times a second in a saturated field, and the ring holds 64
 * records: unthrottled it would erase the run inside four seconds. One row every five
 * seconds keeps the fact in the log — which is where a player goes *after* the banner has
 * gone, to find out what happened — without the log becoming a single repeated line.
 */
const TRIM_LOG_GAP_TICKS = 300;

export class EventLogStore {
  private ring: LogRecord[] = [];
  private head = 0;
  private count = 0;
  /** Bumped on every write so a view can skip a re-render it does not need. */
  private rev = 0;
  private lastTrimTick = -TRIM_LOG_GAP_TICKS;

  constructor() {
    for (let i = 0; i < CAPACITY; i++) this.ring.push({ kind: LogKind.None, tick: 0, id: '', a: 0, b: 0 });
  }

  get revision(): number {
    return this.rev;
  }

  get size(): number {
    return this.count;
  }

  reset(): void {
    for (let i = 0; i < CAPACITY; i++) {
      const r = this.ring[i];
      r.kind = LogKind.None;
      r.tick = 0;
      r.id = '';
      r.a = 0;
      r.b = 0;
    }
    this.head = 0;
    this.count = 0;
    this.lastTrimTick = -TRIM_LOG_GAP_TICKS;
    this.rev++;
  }

  /** Zero-allocation write. Overwrites the oldest slot once the ring is full. */
  note(kind: LogKind, tick: number, id: string, a: number, b: number): void {
    const r = this.ring[this.head];
    r.kind = kind;
    r.tick = tick;
    r.id = id;
    r.a = a;
    r.b = b;
    this.head = (this.head + 1) % CAPACITY;
    if (this.count < CAPACITY) this.count++;
    this.rev++;
  }

  /** Newest first — the log reads like a console, most recent at the top. */
  forEachNewestFirst(fn: (r: Readonly<LogRecord>, index: number) => void): void {
    for (let i = 0; i < this.count; i++) {
      const at = (this.head - 1 - i + CAPACITY * 2) % CAPACITY;
      fn(this.ring[at], i);
    }
  }

  /**
   * Subscribes read-only to the bus for the life of a run. Returns the unsubscribe.
   * `xp_mote` pickups are deliberately dropped: they land several times a second and
   * would bury every line that matters.
   */
  attach(bus: EventBus, tickOf: () => number): () => void {
    const offs: (() => void)[] = [
      bus.on('level_up', (p) => this.note(LogKind.LevelUp, tickOf(), '', p.level, 0)),
      bus.on('levelup_pick', (p) => this.note(LogKind.Pick, tickOf(), p.picked, p.level, 0)),
      bus.on('evolution', (p) => this.note(LogKind.Evolution, tickOf(), p.evolution_id, 0, 0)),
      bus.on('surge_change', (p) => this.note(LogKind.SurgeChange, tickOf(), '', p.from, p.to)),
      bus.on('boss_spawn', (p) => this.note(LogKind.BossSpawn, tickOf(), p.boss_id, 0, 0)),
      bus.on('boss_defeated', (p) => this.note(LogKind.BossDefeated, tickOf(), p.boss_id, p.fight_duration_s, 0)),
      bus.on('phase_change', (p) => this.note(LogKind.PhaseChange, tickOf(), p.phase_id, 0, 0)),
      bus.on('death', (p) => {
        if (!p.elite) return; // trash deaths are the whole run; elites are the story
        this.note(LogKind.EliteDown, tickOf(), p.def_id, p.threat, 0);
      }),
      bus.on('hazard', (p) => {
        if (p.kind !== 'activate') return;
        this.note(LogKind.Hazard, tickOf(), p.hazard_id, Math.round(p.radius), 0);
      }),
      bus.on('pickup', (p) => {
        if (p.kind === 'xp_mote') return;
        this.note(LogKind.Pickup, tickOf(), p.kind, p.value, 0);
      }),
      // AMD-34 — boss waves only. A 20-minute run is 40 waves and the ring holds 64
      // records, so logging every boundary would bury the whole run under its own
      // metronome. The four waves that change what you are fighting earn a row.
      bus.on('wave_change', (p) => {
        if (!p.boss_wave) return;
        this.note(LogKind.WaveChange, tickOf(), '', p.wave, 1);
      }),
      // AMD-35 — every Graft. Seven a run at the reference pilot's income.
      bus.on('gacha_pull', (p) => {
        this.note(LogKind.Graft, tickOf(), p.display_name, p.level_from, p.level_to);
      }),
      // AMD-51 — both edges: the start warns, the end releases.
      bus.on('anomaly', (p) => {
        this.note(LogKind.Anomaly, tickOf(), p.name, p.danger, p.phase === 'start' ? 1 : 0);
      }),
      // AMD-53 — one row per boss, right after its defeat row.
      bus.on('salvage', (p) => {
        this.note(LogKind.Salvage, tickOf(), p.tier, p.ichor, 0);
      }),
      // AMD-46 — the projectile budget clipped a boon's burst. Throttled hard: the
      // event is a symptom of a full field, and a full field produces it continuously.
      bus.on('budget_trim', (p) => {
        // R2 only emits when it actually refused something, but a row that says
        // "4 of 4" would read as a fault report for a boon that is working, so the
        // log does not take the emitter's word for it. Same guard the HUD applies.
        if (p.spawned >= p.requested) return;
        const t = tickOf();
        if (t - this.lastTrimTick < TRIM_LOG_GAP_TICKS) return;
        this.lastTrimTick = t;
        this.note(LogKind.BudgetTrim, t, p.source, p.requested, p.spawned);
      }),
    ];
    return () => {
      for (let i = 0; i < offs.length; i++) offs[i]();
    };
  }
}

interface RowRefs {
  root: HTMLElement;
  glyph: SVGUseElement;
  time: HTMLElement;
  subject: HTMLElement;
  delta: HTMLElement;
}

const FILTERS: readonly { id: 'all' | LogChannel; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'build', label: 'Build' },
  { id: 'threat', label: 'Threat' },
  { id: 'world', label: 'World' },
];

/**
 * The rendered log. Rows are built once and reused; a filter change only flips
 * `hidden` and rewrites text on rows that were already in the tree.
 */
export class EventLogView {
  readonly root: HTMLElement;
  private rows: RowRefs[] = [];
  private list: HTMLElement;
  private emptyNote: HTMLElement;
  private tabs: HTMLButtonElement[] = [];
  private filter: 'all' | LogChannel = 'all';
  private store: EventLogStore | null = null;
  private visible: number;

  constructor(visibleRows = 12) {
    this.visible = visibleRows;
    this.root = div('event-log');

    const head = div('event-log-head');
    head.appendChild(div('group-title', 'Event log'));
    this.root.appendChild(head);

    this.list = div('event-log-list');
    this.root.appendChild(this.list);

    for (let i = 0; i < visibleRows; i++) {
      const row = div('log-row');
      row.hidden = true;
      const svg = icon('circle-notch', 'log-icon');
      const use = svg.firstElementChild as SVGUseElement;
      const time = span('log-time', '');
      const subject = span('log-subject', '');
      const delta = span('log-delta', '');
      // Subject and delta share one grid column so that when the delta cannot fit
      // beside the name it wraps *under* it, inside the column, and the icon and
      // timestamp columns to its left stay a straight vertical ruler.
      const bodyCell = span('log-body');
      bodyCell.appendChild(subject);
      bodyCell.appendChild(delta);
      row.appendChild(svg);
      row.appendChild(time);
      row.appendChild(bodyCell);
      this.list.appendChild(row);
      this.rows.push({ root: row, glyph: use, time, subject, delta });
    }

    this.emptyNote = div('log-empty', 'No events yet. The trench is quiet.');
    this.list.appendChild(this.emptyNote);

    const strip = div('log-filters');
    strip.setAttribute('role', 'tablist');
    for (let i = 0; i < FILTERS.length; i++) {
      const f = FILTERS[i];
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'log-filter' + (f.id === 'all' ? ' is-on' : '');
      b.textContent = f.label;
      b.dataset.filter = f.id;
      b.setAttribute('role', 'tab');
      b.setAttribute('aria-selected', String(f.id === 'all'));
      b.addEventListener('click', () => this.setFilter(f.id));
      strip.appendChild(b);
      this.tabs.push(b);
    }
    this.root.appendChild(strip);
  }

  /** Focus stops for gamepad nav — the tab strip, in reading order. */
  focusStops(): HTMLElement[] {
    return this.tabs;
  }

  private setFilter(id: 'all' | LogChannel): void {
    if (this.filter === id) return;
    this.filter = id;
    for (let i = 0; i < this.tabs.length; i++) {
      const on = this.tabs[i].dataset.filter === id;
      this.tabs[i].classList.toggle('is-on', on);
      this.tabs[i].setAttribute('aria-selected', String(on));
    }
    if (this.store) this.render(this.store);
  }

  /** Called when a panel opens. Never called during a run. */
  render(store: EventLogStore): void {
    this.store = store;
    let out = 0;
    const wanted = this.filter;
    store.forEachNewestFirst((r) => {
      if (out >= this.visible) return;
      if (r.kind === LogKind.None) return;
      const channel = CHANNEL_OF[r.kind];
      if (wanted !== 'all' && channel !== wanted) return;
      const row = this.rows[out];
      row.root.hidden = false;
      row.root.dataset.channel = channel;
      row.glyph.setAttribute('href', '#i-' + ICON_OF[r.kind]);
      row.time.textContent = formatTime(r.tick / 60);
      const subject = subjectOf(r.kind, r.id);
      row.subject.textContent = subject;
      // The subject column truncates with an ellipsis, so the row keeps the full text.
      row.root.title = subject;
      row.delta.textContent = deltaOf(r.kind, r.id, r.a, r.b);
      row.delta.className = 'log-delta is-' + TONE_OF[r.kind];
      out++;
    });
    for (let i = out; i < this.rows.length; i++) this.rows[i].root.hidden = true;
    this.emptyNote.hidden = out > 0;
  }
}

function subjectOf(kind: LogKind, id: string): string {
  switch (kind) {
    case LogKind.LevelUp:
      return 'Lumen level';
    case LogKind.SurgeChange:
      return 'Lumensurge';
    case LogKind.Pick:
    case LogKind.Evolution:
      return contentName(id);
    case LogKind.BossSpawn:
    case LogKind.BossDefeated: {
      const b = bossById.get(id);
      return b ? String(b.display_name) : humanizeId(id);
    }
    case LogKind.EliteDown: {
      const e = enemyById.get(id);
      return e ? e.display_name : humanizeId(id);
    }
    case LogKind.Hazard:
      return humanizeId(id);
    case LogKind.Pickup:
      return id === 'health' ? 'Bioluminescent cache' : 'Powerup';
    case LogKind.PhaseChange:
      return humanizeId(id);
    case LogKind.WaveChange:
      return 'Boss wave';
    case LogKind.Graft:
      return id;
    case LogKind.BudgetTrim:
      return TRIM_SUBJECTS[id] ?? humanizeId(id);
    case LogKind.Anomaly:
      return id;
    case LogKind.Salvage:
      return id === 'jackpot' ? 'Salvage JACKPOT' : id === 'pair' ? 'Salvage find' : 'Salvage scraps';
    default:
      return '';
  }
}

/** AMD-46 — the boon behind a `budget_trim` source key (§6.6). */
const TRIM_SUBJECTS: Readonly<Record<string, string>> = {
  split: 'Mitotic Bloom',
  cascade: 'Cascade Ganglion',
};

/** Bracketed, coloured, and always present — the delta is the point of the row. */
function deltaOf(kind: LogKind, id: string, a: number, b: number): string {
  switch (kind) {
    case LogKind.LevelUp:
      return '(Lv ' + formatInt(a - 1) + ' → ' + formatInt(a) + ')';
    case LogKind.Pick:
      return '(picked @ Lv ' + formatInt(a) + ')';
    case LogKind.Evolution:
      return '(evolved)';
    case LogKind.SurgeChange:
      return '(×' + formatInt(a) + ' → ×' + formatInt(b) + ' surge)';
    case LogKind.BossSpawn:
      return '(boss inbound)';
    case LogKind.BossDefeated:
      return '(' + a.toFixed(1) + 's fight)';
    case LogKind.EliteDown:
      return '(elite down, ' + formatInt(a) + ' threat)';
    case LogKind.PhaseChange:
      return '(phase)';
    case LogKind.Hazard:
      return '(r=' + formatInt(a) + ')';
    case LogKind.WaveChange:
      return '(wave ' + formatInt(a) + ')';
    case LogKind.Graft:
      return b > 0 && b !== a ? '(grafted, Lv ' + formatInt(a) + ' → ' + formatInt(b) + ')' : '(grafted)';
    // Says what happened *and* why, because a count on its own reads as a bug report.
    case LogKind.BudgetTrim:
      return '(burst clipped, ' + formatInt(b) + ' of ' + formatInt(a) + ' — field full)';
    case LogKind.Anomaly:
      return b === 1 ? '(anomaly, danger ' + formatInt(a) + ')' : '(anomaly passed)';
    case LogKind.Salvage:
      return '(+' + formatInt(a) + ' ichor)';
    case LogKind.Pickup:
      // `pickups.json` carries no heal amount on the cache itself, so a zero here is
      // data, not a miscount — say "restored" rather than print a meaningless `+0`.
      if (a <= 0) return id === 'health' ? '(restored)' : '(collected)';
      return id === 'health' ? '(+' + formatInt(a) + ' HP)' : '(+' + formatInt(a) + ')';
    default:
      return '';
  }
}

function contentName(id: string): string {
  const w = weaponById.get(id);
  if (w) return w.display_name;
  const p = passiveById.get(id);
  if (p) return String(p.display_name);
  const e = evolutionById.get(id);
  if (e) return String(e.display_name);
  return humanizeId(id);
}
